if (!customElements.get('anniversary-sticky-subscribe')) {
  class AnniversaryStickySubscribe extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.observer?.disconnect();
      this.sourceObserver?.disconnect();
      this.formObserver?.disconnect();
      cancelAnimationFrame(this.frame);

      this.abortController = new AbortController();
      this.successOrigin = this.dataset.successOrigin;
      this.formMount = this.querySelector('[data-form-mount]');
      this.sourceMount = this.getSourceMount(this.dataset.formClass);
      this.mobileConsentDialog = this.querySelector('[data-mobile-consent-dialog]');
      this.mobileConsentGranted = false;
      this.consentSubmitArmed = false;

      this.bindFormMount(this.sourceMount, 'banner');
      this.bindFormMount(this.formMount, 'sticky');

      if (this.dataset.showBar === 'false') {
        this.setVisibility(false);
        return;
      }
      this.addEventListener('click', this.handleMobileConsentClick, {
        capture: true,
        signal: this.abortController.signal,
      });
      this.addEventListener('submit', this.handleMobileConsentSubmit, {
        capture: true,
        signal: this.abortController.signal,
      });
      this.mobileConsentDialog?.addEventListener('click', this.handleConsentBackdropClick, {
        signal: this.abortController.signal,
      });

      const defaultTarget = this.getTargetSection(this.dataset.triggerSectionId);
      const desktopTarget = this.getTargetSection(this.dataset.desktopTriggerSectionId);
      if ((!defaultTarget && !desktopTarget) || !('IntersectionObserver' in window)) {
        this.setVisibility(true);
        return;
      }

      this.defaultTarget = defaultTarget;
      this.desktopTarget = desktopTarget;
      this.desktopQuery = window.matchMedia('(min-width: 1280px)');
      this.updateVisibility();
      window.addEventListener('scroll', this.handleViewportChange, {
        passive: true,
        signal: this.abortController.signal,
      });
      window.addEventListener('resize', this.handleViewportChange, {
        passive: true,
        signal: this.abortController.signal,
      });
      this.desktopQuery.addEventListener('change', this.handleViewportChange);
      this.observer = new IntersectionObserver(() => this.updateVisibility(), { threshold: 0 });
      if (this.defaultTarget) this.observer.observe(this.defaultTarget);
      if (this.desktopTarget && this.desktopTarget !== this.defaultTarget) {
        this.observer.observe(this.desktopTarget);
      }
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = undefined;
      this.observer?.disconnect();
      this.observer = undefined;
      this.desktopQuery?.removeEventListener('change', this.handleViewportChange);
      this.sourceObserver?.disconnect();
      this.sourceObserver = undefined;
      this.formObserver?.disconnect();
      this.formObserver = undefined;
      cancelAnimationFrame(this.frame);
      this.frame = undefined;
      this.sourceMount = undefined;
      this.formMount = undefined;
      this.defaultTarget = undefined;
      this.desktopTarget = undefined;
      this.desktopQuery = undefined;
      this.pendingSubmitOrigin = undefined;
      this.pendingModalEmail = undefined;
      this.mobileConsentDialog?.close();
      this.mobileConsentDialog = undefined;
      this.mobileSubmitButton = undefined;
      this.mobileConsentGranted = false;
      this.consentSubmitArmed = false;
    }

    /**
     * @returns {boolean}
     */
    get isLuba4() {
      return this.dataset.uiVariant === 'luba4';
    }

    getTargetSection(sectionId) {
      if (!sectionId) return undefined;

      const exactTarget = document.getElementById(`shopify-section-${sectionId}`);

      if (exactTarget) return exactTarget;

      return Array.from(document.querySelectorAll('[id]')).find((element) => element.id.endsWith(sectionId));
    }

    getSourceMount(formClass) {
      if (!formClass) return undefined;

      return Array.from(document.getElementsByClassName(formClass)).find((element) => !this.contains(element));
    }

    /**
     * @returns {HTMLInputElement | null}
     */
    getEmailInput() {
      return (
        this.formMount?.querySelector('input[autocomplete="email"], input[type="email"]') ||
        this.sourceMount?.querySelector('input[autocomplete="email"], input[type="email"]') ||
        null
      );
    }

    /**
     * @returns {HTMLElement | null}
     */
    getStickySubmitButton() {
      return (
        this.querySelector(
          '.anniversary-sticky-subscribe__form .needsclick form button.klaviyo-form-button',
        ) ||
        this.querySelector(
          '.anniversary-sticky-subscribe__form .needsclick form button[type="submit"], .anniversary-sticky-subscribe__form .needsclick form input[type="submit"]',
        )
      );
    }

    /**
     * Legacy fallback: submit through the existing Klaviyo mount so the page
     * does not embed the same Form ID twice.
     * @param {string} email
     * @returns {boolean} True when the Klaviyo submit was triggered or queued.
     */
    submitEmail(email) {
      const trimmed = typeof email === 'string' ? email.trim() : '';
      if (!trimmed) return false;

      this.pendingModalEmail = trimmed;
      this.flushPendingModalEmail();
      return true;
    }

    /**
     * Prepare a new hero-modal submission without clearing the sticky component's
     * remembered success state.
     */
    prepareModalSubmission() {
      if (!this.isLuba4) return;

      this.pendingModalEmail = undefined;
      this.pendingSubmitOrigin = undefined;
      this.mobileConsentGranted = false;
      this.consentSubmitArmed = false;
    }

    /**
     * Preserve the shared subscribed state after the standalone LUBA 4 modal
     * completes through Klaviyo's Client Subscription API.
     */
    recordModalSuccess() {
      if (!this.isLuba4) return;

      if (!this.successOrigin) {
        this.successOrigin = 'modal';
        this.dataset.successOrigin = 'modal';
      }

      this.pendingSubmitOrigin = undefined;
      this.mobileConsentDialog?.close();
      this.setVisibility(false);

      this.dispatchEvent(
        new CustomEvent('theme-subscribe:success', {
          bubbles: true,
          detail: { origin: 'modal' },
        }),
      );
    }

    /**
     * Fill the injected Klaviyo email field and click its submit control.
     * @returns {boolean}
     */
    flushPendingModalEmail() {
      const email = this.pendingModalEmail;
      if (!email) return false;

      const input = this.getEmailInput();
      const button = this.getStickySubmitButton();
      if (!input || !button) return false;

      this.pendingModalEmail = undefined;
      this.setNativeInputValue(input, email);
      this.consentSubmitArmed = true;
      this.mobileConsentGranted = true;
      this.pendingSubmitOrigin = 'modal';
      requestAnimationFrame(() => {
        button.click();
        queueMicrotask(() => {
          this.consentSubmitArmed = false;
        });
      });
      return true;
    }

    /**
     * Klaviyo inputs are framework-controlled; a native setter plus events keeps the value.
     * @param {HTMLInputElement} input
     * @param {string} value
     */
    setNativeInputValue(input, value) {
      const descriptor = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
      if (descriptor?.set) {
        descriptor.set.call(input, value);
      } else {
        input.value = value;
      }

      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    bindFormMount(mount, origin) {
      if (!mount) return;

      mount.addEventListener(
        'click',
        (event) => {
          if (event.target instanceof Element && event.target.closest('button')) {
            if (this.pendingSubmitOrigin !== 'modal') this.pendingSubmitOrigin = origin;
          }
        },
        { signal: this.abortController.signal },
      );
      mount.addEventListener('submit', () => {
        if (this.pendingSubmitOrigin !== 'modal') this.pendingSubmitOrigin = origin;
      }, { signal: this.abortController.signal });

      const observer = new MutationObserver(() => this.handleFormMutation());
      observer.observe(mount, { childList: true, subtree: true });
      if (origin === 'banner') this.sourceObserver = observer;
      if (origin === 'sticky') this.formObserver = observer;
    }

    handleViewportChange = () => {
      if (this.frame) return;

      this.frame = requestAnimationFrame(() => {
        this.frame = undefined;
        this.updateVisibility();
      });
    };

    isConsentRequired() {
      return Boolean(this.mobileConsentDialog);
    }

    /**
     * @param {HTMLElement | null | undefined} submitButton
     */
    openMobileConsentDialog(submitButton) {
      if (!this.isConsentRequired()) return;
      if (this.successOrigin) {
        this.openSuccessDialog();
        return;
      }

      this.mobileSubmitButton = submitButton;
      if (this.mobileConsentDialog && !this.mobileConsentDialog.open) {
        this.mobileConsentDialog.showModal();
        this.mobileConsentDialog.focus({ preventScroll: true });
      }
    }

    handleConsentBackdropClick = (event) => {
      if (event.target !== this.mobileConsentDialog) return;
      this.mobileSubmitButton = undefined;
      this.mobileConsentDialog?.close();
    };

    handleMobileConsentClick = (event) => {
      if (!(event.target instanceof Element)) return;

      const agreeButton = event.target.closest('[data-mobile-consent-agree]');
      if (agreeButton) {
        event.preventDefault();
        this.mobileConsentGranted = true;
        this.consentSubmitArmed = true;
        const submitButton = this.mobileSubmitButton || this.getStickySubmitButton();
        this.mobileSubmitButton = undefined;
        this.mobileConsentDialog?.close();
        requestAnimationFrame(() => {
          submitButton?.click();
          queueMicrotask(() => {
            this.consentSubmitArmed = false;
          });
        });
        return;
      }

      const cancelButton = event.target.closest('[data-mobile-consent-cancel]');
      if (cancelButton) {
        event.preventDefault();
        this.mobileSubmitButton = undefined;
        this.mobileConsentDialog?.close();
        return;
      }

      const submitButton = event.target.closest(
        '.anniversary-sticky-subscribe__form .needsclick form button',
      );
      if (!submitButton || !this.isConsentRequired()) return;

      if (this.isLuba4 && this.successOrigin) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.openSuccessDialog();
        return;
      }

      if (this.isLuba4) {
        if (this.consentSubmitArmed) {
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();
        this.openMobileConsentDialog(submitButton);
        return;
      }

      if (this.mobileConsentGranted) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.openMobileConsentDialog(submitButton);
    };

    handleMobileConsentSubmit = (event) => {
      if (!this.isConsentRequired()) return;

      if (this.isLuba4 && this.successOrigin) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.openSuccessDialog();
        return;
      }

      if (this.isLuba4) {
        if (this.consentSubmitArmed) {
          this.consentSubmitArmed = false;
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();
        this.openMobileConsentDialog(event.submitter || this.getStickySubmitButton());
        return;
      }

      if (this.mobileConsentGranted) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.openMobileConsentDialog(event.submitter || this.getStickySubmitButton());
    };

    /**
     * Desktop uses 1280px, matching the theme breakpoint.
     * @returns {boolean}
     */
    isDesktopViewport() {
      return Boolean(this.desktopQuery?.matches);
    }

    /**
     * @returns {Element | undefined}
     */
    getActiveTriggerTarget() {
      if (this.isDesktopViewport() && this.desktopTarget) return this.desktopTarget;
      return this.defaultTarget;
    }

    updateVisibility() {
      const target = this.getActiveTriggerTarget();
      if (!target) return;

      const isPastTrigger = target.getBoundingClientRect().bottom <= 0;
      this.setVisibility(isPastTrigger);
    }

    setVisibility(isVisible) {
      if (this.successOrigin === 'banner') isVisible = false;
      if (this.isLuba4 && this.successOrigin) isVisible = false;

      this.dataset.state = isVisible ? 'visible' : 'hidden';
      this.syncFormState();
    }

    handleFormMutation() {
      this.setSuccessOrigin();
      this.flushPendingModalEmail();
      this.syncFormState();
    }

    setSuccessOrigin() {
      const sourceSuccess = this.isSuccessState(this.sourceMount);
      const stickySuccess = this.isSuccessState(this.formMount);
      if (!sourceSuccess && !stickySuccess) return;

      const detectedOrigin = this.pendingSubmitOrigin || (sourceSuccess ? 'banner' : 'sticky');
      const isFirstSuccess = !this.successOrigin;

      if (isFirstSuccess) {
        this.successOrigin = detectedOrigin;
        this.dataset.successOrigin = this.successOrigin;
      }

      if (this.isLuba4) {
        this.mobileConsentDialog?.close();
        this.setVisibility(false);
      }

      if (!isFirstSuccess && (!this.isLuba4 || detectedOrigin !== 'modal')) return;

      this.pendingSubmitOrigin = undefined;

      /**
       * theme-subscribe:success
       * @event theme-subscribe:success
       * @property {{origin: string}} detail - Which surface submitted: banner, sticky, or modal.
       */
      this.dispatchEvent(
        new CustomEvent('theme-subscribe:success', {
          bubbles: true,
          detail: { origin: detectedOrigin },
        }),
      );
    }

    /**
     * Open the shared LUBA 4 success dialog.
     */
    openSuccessDialog() {
      const success = document.querySelector('luba4-subscribe-success');
      if (success && typeof success.open === 'function') success.open();
    }

    isSuccessState(mount) {
      if (!mount) return false;

      const emailInput = mount.querySelector('input[autocomplete="email"], input[type="email"]');
      const successHeading = mount.querySelector('[data-testid="form-component"] h1');

      return !emailInput && Boolean(successHeading);
    }

    syncFormState() {
      if (this.sourceMount) {
        this.sourceMount.hidden = this.successOrigin === 'sticky' || Boolean(this.isLuba4 && this.successOrigin);
        this.applyFormPresentation(this.sourceMount);
      }

      if (this.formMount) this.applyFormPresentation(this.formMount);
    }

    applyFormPresentation(mount) {
      const placeholder = this.dataset.emailPlaceholder?.trim();
      const capitalized = placeholder?.replace(/^\S/, (char) => char.toLocaleUpperCase());
      const styleProps = [
        'font',
        'font-size',
        'font-family',
        'font-weight',
        'line-height',
        'letter-spacing',
        'border-radius',
        'border-top-left-radius',
        'border-top-right-radius',
        'border-bottom-right-radius',
        'border-bottom-left-radius',
        'border-start-start-radius',
        'border-start-end-radius',
        'border-end-end-radius',
        'border-end-start-radius',
      ];

      mount
        .querySelectorAll(
          'input[autocomplete="email"], input[type="email"], button, [data-testid="form-component"]',
        )
        .forEach((element) => {
          if (capitalized && element.matches('input[autocomplete="email"], input[type="email"]')) {
            element.setAttribute('placeholder', capitalized);
          }
          if (element.matches('button')) {
            element.classList.add('anniversary-sticky-subscribe__submit');
          }
          styleProps.forEach((property) => element.style.removeProperty(property));
        });
    }
  }

  customElements.define('anniversary-sticky-subscribe', AnniversaryStickySubscribe);
}
